import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import type { ChangeEvent } from 'react';
// Menu catalogue backed by Supabase (categories + menu_items)
import {
  fetchMenu,
  fetchCategories,
  upsertMenuItem,
  updateMenuItem,
  deleteMenuItem,
  bulkImportMenuItems,
  addCategory,
  updateCategory,
  deleteCategory,
  type MenuItem,
  type Category,
  type MenuVariant,
  type MenuAddon,
} from '@/lib/data';
import { readCache, writeCache } from '@/lib/cache';
import { useRestaurant } from '@/lib/session';
import {
  downloadMenuTemplate,
  downloadMenuXlsx,
  parseMenuSheet,
  planMenuImport,
  rowToPayload,
  type MenuImportPlanRow,
} from '@/lib/menuSheet';

type FoodType = 'veg' | 'non-veg' | 'egg';

interface VariantForm {
  id: string;
  name: string;
  price: string;
}

interface AddonForm {
  id: string;
  name: string;
  price: string;
}

interface ItemForm {
  name: string;
  description: string;
  category: string;
  foodType: FoodType;
  price: string;
  prepTime: string;
  gstRate: string;
  hsnCode: string;
  available: boolean;
  recommended: boolean;
  dineIn: boolean;
  takeaway: boolean;
  online: boolean;
  image: string;
  hasVariants: boolean;
  variants: VariantForm[];
  addons: AddonForm[];
}

function emptyVariant(): VariantForm {
  return { id: `v-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, name: '', price: '' };
}

function emptyAddon(): AddonForm {
  return { id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, name: '', price: '' };
}

function emptyForm(): ItemForm {
  return {
    name: '',
    description: '',
    category: '',
    foodType: 'veg',
    price: '',
    prepTime: '',
    gstRate: '5',
    hsnCode: '9963',
    available: true,
    recommended: false,
    dineIn: true,
    takeaway: true,
    online: true,
    image: '',
    hasVariants: false,
    variants: [],
    addons: [],
  };
}

function itemToForm(item: MenuItem): ItemForm {
  const variants = item.variants ?? [];
  const addons = item.addons ?? [];
  return {
    name: item.name,
    description: item.description ?? '',
    category: item.category ?? '',
    foodType: item.foodType,
    price: item.price ? String(item.price) : '',
    prepTime: item.prepTime ? String(item.prepTime) : '',
    gstRate: String(item.gstRate),
    hsnCode: item.hsnCode || '9963',
    available: item.available,
    recommended: item.recommended,
    dineIn: item.dineIn ?? true,
    takeaway: item.takeaway ?? true,
    online: item.online ?? true,
    image: item.image ?? '',
    hasVariants: variants.length > 0,
    variants: variants.map((v, i) => ({ id: `v-${i}-${Math.random().toString(36).slice(2, 7)}`, name: v.name, price: String(v.price) })),
    addons: addons.map((a, i) => ({ id: `a-${i}-${Math.random().toString(36).slice(2, 7)}`, name: a.name, price: String(a.price) })),
  };
}

const foodTypeOptions: { key: FoodType; label: string; icon: string; activeClass: string }[] = [
  { key: 'veg', label: 'Veg', icon: 'ri-leaf-line', activeClass: 'border-green-600 bg-green-50 text-green-700' },
  { key: 'non-veg', label: 'Non-Veg', icon: 'ri-restaurant-2-line', activeClass: 'border-red-500 bg-red-50 text-red-700' },
  { key: 'egg', label: 'Egg', icon: 'ri-circle-fill', activeClass: 'border-amber-500 bg-amber-50 text-amber-700' },
];

/* Menu import/export uses the shared Excel helper in src/lib/menuSheet.ts, so
   the merchant and superadmin menu pages share an identical template + format.
   The importer diffs against the saved menu and only writes new/changed rows. */

export default function MenuPage() {
  const restaurant = useRestaurant();

  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [showUnavailable, setShowUnavailable] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [bulkActionOpen, setBulkActionOpen] = useState(false);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [form, setForm] = useState<ItemForm>(emptyForm());
  const [imagePreview, setImagePreview] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [deleteTarget, setDeleteTarget] = useState<MenuItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Category management
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [catEdits, setCatEdits] = useState<Record<string, string>>({});
  const [catBusy, setCatBusy] = useState(false);
  const [catError, setCatError] = useState<string | null>(null);

  // CSV import / export
  const csvInputRef = useRef<HTMLInputElement>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState<MenuImportPlanRow[]>([]);
  const [showOnlyChanges, setShowOnlyChanges] = useState(true);
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const catKey = `menu.cats:${restaurant.id}`;
    const menuKey = `menu.items:${restaurant.id}`;
    // Paint instantly from cache when we've loaded this menu before; otherwise
    // show the spinner. Either way we revalidate in the background below.
    const cachedCats = readCache<Category[]>(catKey);
    const cachedMenu = readCache<MenuItem[]>(menuKey);
    if (cachedCats && cachedMenu) {
      setCategories(cachedCats);
      setItems(cachedMenu);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setLoadError(null);
    try {
      const [cats, menu] = await Promise.all([
        fetchCategories(restaurant.id),
        fetchMenu(restaurant.id),
      ]);
      writeCache(catKey, cats);
      writeCache(menuKey, menu);
      setCategories(cats);
      setItems(menu);
    } catch (e) {
      // Keep showing the cached menu if a background refresh fails.
      if (!(cachedCats && cachedMenu)) {
        setLoadError(e instanceof Error ? e.message : 'Failed to load the menu.');
      }
    } finally {
      setLoading(false);
    }
  }, [restaurant.id]);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    let result = items;
    if (activeCategory !== 'all') {
      result = result.filter((i) => i.category === activeCategory);
    }
    if (!showUnavailable) {
      result = result.filter((i) => i.available);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((i) => i.name.toLowerCase().includes(q) || (i.description ?? '').toLowerCase().includes(q));
    }
    return result;
  }, [items, activeCategory, search, showUnavailable]);

  const visibleCount = activeCategory === 'all' ? items.length : items.filter((i) => i.category === activeCategory).length;
  const availableCount = filtered.length;

  // Category id <-> name lookups (items store the category *id*; CSV uses names).
  const catNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of categories) m.set(c.id, c.name);
    return m;
  }, [categories]);
  const catName = useCallback((id: string | null) => (id ? (catNameById.get(id) ?? id) : ''), [catNameById]);

  // Per-category counts computed client-side from the fetched menu
  const countFor = useCallback(
    (catId: string) => (catId === 'all' ? items.length : items.filter((i) => i.category === catId).length),
    [items],
  );

  const categoryTabs = useMemo(
    () => [
      { id: 'all', name: 'All Items', icon: 'ri-apps-line' as string | null },
      ...categories.map((c) => ({ id: c.id, name: c.name, icon: c.icon })),
    ],
    [categories],
  );

  const toggleSelect = (id: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleImagePick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setForm((prev) => ({ ...prev, image: dataUrl }));
      setImagePreview(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setForm((prev) => ({ ...prev, image: '' }));
    setImagePreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openAddModal = () => {
    setEditingItem(null);
    setForm(emptyForm());
    setImagePreview('');
    setFormError(null);
    setModalOpen(true);
  };

  const openEditModal = (item: MenuItem) => {
    setEditingItem(item);
    const f = itemToForm(item);
    setForm(f);
    setImagePreview(item.image || '');
    setFormError(null);
    setModalOpen(true);
  };

  // --- Variant helpers ---
  const addVariant = () => setForm((prev) => ({ ...prev, variants: [...prev.variants, emptyVariant()] }));
  const removeVariant = (id: string) => setForm((prev) => ({ ...prev, variants: prev.variants.filter((v) => v.id !== id) }));
  const updateVariant = (id: string, field: 'name' | 'price', value: string) =>
    setForm((prev) => ({ ...prev, variants: prev.variants.map((v) => (v.id === id ? { ...v, [field]: value } : v)) }));

  // --- Addon helpers ---
  const addAddon = () => setForm((prev) => ({ ...prev, addons: [...prev.addons, emptyAddon()] }));
  const removeAddon = (id: string) => setForm((prev) => ({ ...prev, addons: prev.addons.filter((a) => a.id !== id) }));
  const updateAddon = (id: string, field: 'name' | 'price', value: string) =>
    setForm((prev) => ({ ...prev, addons: prev.addons.map((a) => (a.id === id ? { ...a, [field]: value } : a)) }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.category || saving) return;

    const mappedVariants: MenuVariant[] = form.hasVariants
      ? form.variants.filter((v) => v.name.trim() && v.price).map((v) => ({ name: v.name.trim(), price: Number(v.price) || 0 }))
      : [];

    const mappedAddons: MenuAddon[] = form.addons
      .filter((a) => a.name.trim() && a.price)
      .map((a) => ({ name: a.name.trim(), price: Number(a.price) || 0 }));

    // Display price: base price, or lowest variant price when variants exist
    let displayPrice = Number(form.price) || 0;
    if (form.hasVariants && mappedVariants.length > 0) {
      const prices = mappedVariants.map((v) => v.price).filter((p) => p > 0);
      if (prices.length > 0) displayPrice = Math.min(...prices);
    }

    const payload: Partial<MenuItem> & { name: string } = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      category: form.category,
      foodType: form.foodType,
      price: displayPrice,
      prepTime: Number(form.prepTime) || 10,
      gstRate: Number(form.gstRate),
      hsnCode: form.hsnCode.trim() || '9963',
      available: form.available,
      recommended: form.recommended,
      dineIn: form.dineIn,
      takeaway: form.takeaway,
      online: form.online,
      image: form.image || null,
      variants: mappedVariants,
      addons: mappedAddons,
    };

    setSaving(true);
    setFormError(null);
    try {
      if (editingItem) {
        await updateMenuItem(restaurant.id, editingItem.id, payload);
        setItems((prev) => prev.map((it) => (it.id === editingItem.id ? { ...it, ...payload } : it)));
      } else {
        const created = await upsertMenuItem(restaurant.id, payload);
        setItems((prev) => [...prev, created]);
      }
      setModalOpen(false);
      setEditingItem(null);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to save the item.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: MenuItem) => {
    if (deleting) return;
    setDeleting(true);
    try {
      await deleteMenuItem(restaurant.id, item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      setSelectedItems((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      setDeleteTarget(null);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to delete the item.');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const bulkSetAvailability = async (available: boolean) => {
    const ids = [...selectedItems];
    setBulkActionOpen(false);
    try {
      await Promise.all(ids.map((id) => updateMenuItem(restaurant.id, id, { available })));
      setItems((prev) => prev.map((it) => (ids.includes(it.id) ? { ...it, available } : it)));
      setSelectedItems(new Set());
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to update items.');
    }
  };

  const bulkMarkAvailable = () => { void bulkSetAvailability(true); };
  const bulkMarkUnavailable = () => { void bulkSetAvailability(false); };

  // One-tap availability toggle for a single item (optimistic, reverts on error).
  const toggleAvailability = async (item: MenuItem) => {
    const next = !item.available;
    setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, available: next } : it)));
    try {
      await updateMenuItem(restaurant.id, item.id, { available: next });
    } catch (e) {
      setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, available: item.available } : it)));
      setLoadError(e instanceof Error ? e.message : 'Failed to update availability.');
    }
  };

  // --- Excel/CSV export / template / import (shared menuSheet helper) ---
  const handleExportCsv = () => {
    const rows = filtered.length ? filtered : items;
    if (rows.length === 0) { setLoadError('No items to export.'); return; }
    const stamp = new Date().toISOString().slice(0, 10);
    downloadMenuXlsx(`menu_${restaurant.id}_${stamp}.xlsx`, rows, catName);
  };

  const handleDownloadTemplate = () => downloadMenuTemplate();

  const handleCsvFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    try {
      const parsed = await parseMenuSheet(file);
      // Diff against the saved menu — only new/changed rows will be written.
      const rows = planMenuImport(parsed, items, catName);
      setImportError(rows.length === 0 ? 'No rows found. Use the template and add at least one item.' : null);
      setImportRows(rows);
      setImportOpen(true);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Could not read the file.');
      setImportRows([]);
      setImportOpen(true);
    }
  };

  const runImport = async () => {
    // Only write rows that are actually new or changed — skip unchanged/errors.
    const valid = importRows.filter((r) => r.status === 'new' || r.status === 'update');
    if (valid.length === 0 || importBusy) return;
    setImportBusy(true);
    setImportError(null);
    try {
      // Resolve category names → ids, auto-creating any that don't exist yet.
      const idByName = new Map(categories.map((c) => [c.name.trim().toLowerCase(), c.id]));
      const newCatNames = Array.from(new Set(
        valid.map((r) => (r.row.categoryName ?? '').trim()).filter((c) => c && !idByName.has(c.toLowerCase())),
      ));
      if (newCatNames.length > 0) {
        const created = await Promise.all(newCatNames.map((n) => addCategory(restaurant.id, n)));
        setCategories((prev) => [...prev, ...created]);
        created.forEach((c) => idByName.set(c.name.trim().toLowerCase(), c.id));
      }

      const payloads = valid.map((plan) => {
        const cn = (plan.row.categoryName ?? '').trim();
        const categoryId = cn
          ? idByName.get(cn.toLowerCase()) ?? null
          : plan.existing?.category ?? null;
        return rowToPayload(plan, categoryId);
      });
      const saved = await bulkImportMenuItems(restaurant.id, payloads);
      setItems((prev) => {
        const map = new Map(prev.map((it) => [it.id, it]));
        for (const s of saved) map.set(s.id, s);
        return Array.from(map.values());
      });
      setImportOpen(false);
      setImportRows([]);
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Import failed.');
    } finally {
      setImportBusy(false);
    }
  };

  const bulkDelete = async () => {
    const ids = [...selectedItems];
    setBulkActionOpen(false);
    try {
      await Promise.all(ids.map((id) => deleteMenuItem(restaurant.id, id)));
      setItems((prev) => prev.filter((it) => !ids.includes(it.id)));
      setSelectedItems(new Set());
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to delete items.');
    }
  };

  // --- Category management ---
  const handleAddCategory = async () => {
    const name = newCatName.trim();
    if (!name || catBusy) return;
    setCatBusy(true);
    setCatError(null);
    try {
      const created = await addCategory(restaurant.id, name);
      setCategories((prev) => [...prev, created]);
      setNewCatName('');
    } catch (e) {
      setCatError(e instanceof Error ? e.message : 'Failed to add the category.');
    } finally {
      setCatBusy(false);
    }
  };

  const handleRenameCategory = async (cat: Category) => {
    const name = (catEdits[cat.id] ?? cat.name).trim();
    if (!name || name === cat.name || catBusy) return;
    setCatBusy(true);
    setCatError(null);
    try {
      await updateCategory(restaurant.id, cat.id, { name });
      setCategories((prev) => prev.map((c) => (c.id === cat.id ? { ...c, name } : c)));
    } catch (e) {
      setCatError(e instanceof Error ? e.message : 'Failed to rename the category.');
    } finally {
      setCatBusy(false);
    }
  };

  const handleDeleteCategory = async (cat: Category) => {
    if (catBusy) return;
    const inUse = items.filter((i) => i.category === cat.id).length;
    const message = inUse > 0
      ? `Delete "${cat.name}"? ${inUse} item(s) currently use this category.`
      : `Delete "${cat.name}"?`;
    if (!window.confirm(message)) return;
    setCatBusy(true);
    setCatError(null);
    try {
      await deleteCategory(restaurant.id, cat.id);
      setCategories((prev) => prev.filter((c) => c.id !== cat.id));
      if (activeCategory === cat.id) setActiveCategory('all');
    } catch (e) {
      setCatError(e instanceof Error ? e.message : 'Failed to delete the category.');
    } finally {
      setCatBusy(false);
    }
  };

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-foreground-800">Menu & Items</h2>
          <p className="text-sm text-foreground-500 mt-0.5">{items.length} total items · {items.filter((i) => !i.available).length} unavailable · {categories.length} categories</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
            <input
              type="text"
              placeholder="Search items..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 w-56"
            />
          </div>
          <button
            onClick={handleDownloadTemplate}
            className="px-3 py-2 border border-background-200 text-foreground-700 rounded-md text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            title="Download a blank Excel template with all columns"
          >
            <i className="ri-file-excel-2-line mr-1.5"></i>Template
          </button>
          <button
            onClick={() => csvInputRef.current?.click()}
            className="px-3 py-2 border border-background-200 text-foreground-700 rounded-md text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            title="Import items from an Excel/CSV file"
          >
            <i className="ri-upload-2-line mr-1.5"></i>Import
          </button>
          <button
            onClick={handleExportCsv}
            className="px-3 py-2 border border-background-200 text-foreground-700 rounded-md text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            title="Export the current items to Excel"
          >
            <i className="ri-download-2-line mr-1.5"></i>Export
          </button>
          <input ref={csvInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleCsvFile} className="hidden" />
          <button
            onClick={openAddModal}
            className="px-4 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            + Add Item
          </button>
        </div>
      </div>

      {/* Load error banner */}
      {loadError && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-red-50 border border-red-200">
          <p className="text-sm text-red-700 flex items-center gap-2"><i className="ri-error-warning-line"></i>{loadError}</p>
          <button onClick={() => void load()} className="text-sm font-medium text-red-700 hover:text-red-800 underline cursor-pointer whitespace-nowrap">Retry</button>
        </div>
      )}

      {/* Category tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {categoryTabs.map((cat) => (
          <button
            key={cat.id}
            onClick={() => { setActiveCategory(cat.id); setSelectedItems(new Set()); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors cursor-pointer whitespace-nowrap ${
              activeCategory === cat.id
                ? 'bg-primary-500 text-background-50 border-primary-500'
                : 'bg-background-50 text-foreground-600 border-background-200 hover:border-background-300'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${cat.icon || 'ri-price-tag-3-line'} text-xs`}></i>
            </div>
            {cat.name}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              activeCategory === cat.id ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-500'
            }`}>
              {countFor(cat.id)}
            </span>
          </button>
        ))}
        <button
          onClick={() => { setCatEdits({}); setCatError(null); setCatModalOpen(true); }}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border border-dashed border-background-300 text-foreground-500 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-settings-3-line text-xs"></i>
          Categories
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-foreground-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showUnavailable}
              onChange={(e) => setShowUnavailable(e.target.checked)}
              className="rounded border-background-300 text-primary-500 focus:ring-primary-300 cursor-pointer"
            />
            Show unavailable
          </label>
          {filtered.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-foreground-600 cursor-pointer">
              <input
                type="checkbox"
                checked={filtered.every((i) => selectedItems.has(i.id))}
                onChange={(e) => setSelectedItems((prev) => {
                  const next = new Set(prev);
                  if (e.target.checked) filtered.forEach((i) => next.add(i.id));
                  else filtered.forEach((i) => next.delete(i.id));
                  return next;
                })}
                className="rounded border-background-300 text-primary-500 focus:ring-primary-300 cursor-pointer"
              />
              Select all
            </label>
          )}
          <span className="text-xs text-foreground-500">{availableCount} of {visibleCount} items showing</span>
          <div className="flex items-center gap-0.5 bg-background-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('list')}
              className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors cursor-pointer ${viewMode === 'list' ? 'bg-background-50 text-primary-600 shadow-sm' : 'text-foreground-400 hover:text-foreground-600'}`}
              title="List view"
            >
              <i className="ri-list-check text-sm"></i>
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors cursor-pointer ${viewMode === 'grid' ? 'bg-background-50 text-primary-600 shadow-sm' : 'text-foreground-400 hover:text-foreground-600'}`}
              title="Grid view"
            >
              <i className="ri-grid-fill text-sm"></i>
            </button>
          </div>
        </div>
        {selectedItems.size > 0 && (
          <div className="relative">
            <button
              onClick={() => setBulkActionOpen(!bulkActionOpen)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-secondary-100 text-secondary-800 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span>{selectedItems.size} selected</span>
              <i className="ri-arrow-down-s-line text-sm"></i>
            </button>
            {bulkActionOpen && (
              <div className="absolute right-0 top-full mt-1 w-44 bg-background-50 border border-background-200 rounded-lg shadow-lg z-30 py-1">
                <button onClick={bulkMarkAvailable} className="w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Mark as Available</button>
                <button onClick={bulkMarkUnavailable} className="w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Mark Unavailable (86)</button>
                <button onClick={() => void bulkDelete()} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap">Delete Selected</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="py-16 text-center">
          <i className="ri-loader-4-line text-2xl text-primary-500 animate-spin"></i>
          <p className="text-sm text-foreground-500 mt-2">Loading menu...</p>
        </div>
      )}

      {/* Items — List view */}
      {!loading && viewMode === 'list' && filtered.length > 0 && (
        <div className="rounded-lg border border-background-200 overflow-hidden divide-y divide-background-100">
          {filtered.map((item) => (
            <MenuItemRow
              key={item.id}
              item={item}
              categoryName={catName(item.category)}
              selected={selectedItems.has(item.id)}
              onToggleSelect={() => toggleSelect(item.id)}
              onToggleAvailable={() => toggleAvailability(item)}
              onEdit={() => openEditModal(item)}
              onDelete={() => setDeleteTarget(item)}
            />
          ))}
        </div>
      )}

      {/* Items — Grid view */}
      {!loading && viewMode === 'grid' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {filtered.map((item) => (
            <MenuItemCard
              key={item.id}
              item={item}
              selected={selectedItems.has(item.id)}
              onToggleSelect={() => toggleSelect(item.id)}
              onToggleAvailable={() => toggleAvailability(item)}
              onEdit={() => openEditModal(item)}
              onDelete={() => setDeleteTarget(item)}
            />
          ))}
        </div>
      )}

      {!loading && !loadError && filtered.length === 0 && (
        <div className="py-16 text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-restaurant-2-line text-xl text-foreground-300"></i>
          </div>
          {items.length === 0 ? (
            <>
              <p className="text-sm text-foreground-500">No items yet — add your first dish</p>
              <button
                onClick={openAddModal}
                className="mt-3 px-4 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                + Add Item
              </button>
            </>
          ) : (
            <p className="text-sm text-foreground-500">No items in this category</p>
          )}
        </div>
      )}

      {/* ───── CSV Import Preview Modal ───── */}
      {importOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[6vh]">
          <div className="absolute inset-0 bg-black/30" onClick={() => { if (!importBusy) { setImportOpen(false); setImportRows([]); } }}></div>
          <div className="relative w-full max-w-3xl bg-background-50 rounded-xl shadow-lg mx-4 animate-[slideInUp_0.2s_ease-out] max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
              <div>
                <h3 className="text-base font-bold text-foreground-800">Import items from CSV</h3>
                <p className="text-xs text-foreground-500 mt-0.5">Review before importing. Rows are matched to existing items by name.</p>
              </div>
              <button onClick={() => { if (!importBusy) { setImportOpen(false); setImportRows([]); } }} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            {importError && (
              <p className="mx-5 mt-3 text-sm text-red-600 flex items-center gap-1.5"><i className="ri-error-warning-line"></i>{importError}</p>
            )}

            {importRows.length > 0 && (() => {
              const news = importRows.filter((r) => r.status === 'new').length;
              const updates = importRows.filter((r) => r.status === 'update').length;
              const unchanged = importRows.filter((r) => r.status === 'unchanged').length;
              const errors = importRows.filter((r) => r.status === 'error').length;
              const toWrite = news + updates;
              const visible = showOnlyChanges ? importRows.filter((r) => r.status !== 'unchanged') : importRows;
              return (
                <>
                  <div className="flex items-center gap-2 px-5 py-3 text-xs flex-wrap">
                    <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">{news} new</span>
                    <span className="px-2 py-1 rounded-full bg-secondary-100 text-secondary-700 font-medium">{updates} changed</span>
                    {unchanged > 0 && <span className="px-2 py-1 rounded-full bg-background-100 text-foreground-500 font-medium">{unchanged} unchanged · skipped</span>}
                    {errors > 0 && <span className="px-2 py-1 rounded-full bg-red-100 text-red-700 font-medium">{errors} invalid</span>}
                    {unchanged > 0 && (
                      <label className="ml-auto flex items-center gap-1.5 text-foreground-500 cursor-pointer">
                        <input type="checkbox" checked={showOnlyChanges} onChange={(e) => setShowOnlyChanges(e.target.checked)} className="rounded border-background-300 text-primary-500 cursor-pointer" />
                        Only show new &amp; changed
                      </label>
                    )}
                  </div>
                  <div className="flex-1 overflow-y-auto px-5">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-background-50">
                        <tr className="text-left text-foreground-500 border-b border-background-200">
                          <th className="py-2 font-medium w-24">Status</th>
                          <th className="py-2 font-medium">Name</th>
                          <th className="py-2 font-medium">What changes</th>
                          <th className="py-2 font-medium text-right">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visible.map((r, i) => (
                          <tr key={i} className={`border-b border-background-100 ${r.status === 'unchanged' ? 'opacity-50' : ''}`}>
                            <td className="py-2">
                              {r.status === 'new' && <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">New</span>}
                              {r.status === 'update' && <span className="text-xs px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700">Changed</span>}
                              {r.status === 'unchanged' && <span className="text-xs px-1.5 py-0.5 rounded bg-background-100 text-foreground-500">No change</span>}
                              {r.status === 'error' && <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700">Invalid</span>}
                            </td>
                            <td className="py-2 text-foreground-800">{r.row.name}</td>
                            <td className="py-2 text-foreground-600 text-xs">
                              {r.status === 'error' && <span className="text-red-600">{r.message}</span>}
                              {r.status === 'new' && <span className="text-foreground-400">Will be created</span>}
                              {r.status === 'update' && r.changed.join(', ')}
                              {r.status === 'unchanged' && <span className="text-foreground-400">Identical — skipped</span>}
                            </td>
                            <td className="py-2 text-right text-foreground-700">{r.status === 'error' ? '' : `₹${r.row.price}`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-background-200">
                    <button onClick={() => { setImportOpen(false); setImportRows([]); }} disabled={importBusy} className="px-5 py-2 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50">Cancel</button>
                    <button onClick={() => void runImport()} disabled={importBusy || toWrite === 0} className="px-5 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap">
                      {importBusy ? 'Importing…' : toWrite === 0 ? 'Nothing to import' : `Import ${toWrite} change${toWrite === 1 ? '' : 's'}`}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* ───── Add / Edit Modal ───── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[4vh]">
          <div className="absolute inset-0 bg-black/30" onClick={() => { setModalOpen(false); setEditingItem(null); }}></div>
          <div className="relative w-full max-w-3xl bg-background-50 rounded-xl shadow-lg mx-4 animate-[slideInUp_0.2s_ease-out] max-h-[94vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-background-50 border-b border-background-200 px-5 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="font-semibold text-foreground-800">{editingItem ? 'Edit Item' : 'Add New Item'}</h3>
                <p className="text-xs text-foreground-400 mt-0.5">Fill in the details below to {editingItem ? 'update' : 'create'} a menu item</p>
              </div>
              <button
                onClick={() => { setModalOpen(false); setEditingItem(null); }}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            <div className="p-5 space-y-6">
              {/* ── Section 1: Image ── */}
              <SectionLabel icon="ri-image-line" title="Item Image" />
              {imagePreview ? (
                <div className="relative w-full h-52 rounded-lg overflow-hidden border border-background-200 group/image">
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-black/0 group-hover/image:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover/image:opacity-100">
                    <button onClick={() => fileInputRef.current?.click()} className="px-3 py-1.5 text-xs font-medium rounded-md bg-background-50 text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-image-edit-line mr-1"></i>Change</button>
                    <button onClick={removeImage} className="px-3 py-1.5 text-xs font-medium rounded-md bg-red-500 text-background-50 hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-delete-bin-line mr-1"></i>Remove</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => fileInputRef.current?.click()} className="w-full h-48 rounded-lg border-2 border-dashed border-background-300 hover:border-primary-300 bg-background-50 hover:bg-background-100 transition-all flex flex-col items-center justify-center gap-3 cursor-pointer group/upload">
                  <div className="w-12 h-12 rounded-full bg-background-100 group-hover/upload:bg-primary-100 flex items-center justify-center transition-colors">
                    <i className="ri-image-add-line text-xl text-foreground-400 group-hover/upload:text-primary-500 transition-colors"></i>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-foreground-600">Upload Image</p>
                    <p className="text-xs text-foreground-400 mt-0.5">PNG, JPG or WebP up to 5MB</p>
                  </div>
                </button>
              )}
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleImagePick} className="hidden" />

              {/* ── Section 2: Basic Info ── */}
              <SectionLabel icon="ri-file-list-3-line" title="Basic Information" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Item Name *</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Butter Chicken" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Category *</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer">
                    <option value="">Select category</option>
                    {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Food Type *</label>
                  <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
                    {foodTypeOptions.map((opt) => (
                      <button
                        key={opt.key}
                        onClick={() => setForm({ ...form, foodType: opt.key })}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap border ${
                          form.foodType === opt.key
                            ? `${opt.activeClass}`
                            : 'border-transparent text-foreground-500 hover:text-foreground-700'
                        }`}
                      >
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className={opt.icon}></i>
                        </div>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Description</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} maxLength={500} placeholder="Detailed description of the dish..." className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 resize-none focus:outline-none focus:border-primary-300"></textarea>
                </div>
              </div>

              {/* ── Section 3: Variants ── */}
              <SectionLabel icon="ri-stack-line" title="Variants & Pricing" subtitle="Add portion sizes or variants with different prices" />
              <div className="flex items-center gap-3 mb-3">
                <label className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.hasVariants}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForm((prev) => ({
                        ...prev,
                        hasVariants: checked,
                        variants: checked ? (prev.variants.length > 0 ? prev.variants : [emptyVariant(), emptyVariant()]) : [],
                      }));
                    }}
                    className="rounded border-background-300 text-primary-500 focus:ring-primary-300 cursor-pointer"
                  />
                  This item has multiple variants (e.g. Half/Full, Small/Large)
                </label>
              </div>

              {form.hasVariants ? (
                <div className="space-y-2">
                  {form.variants.map((v, idx) => (
                    <div key={v.id} className="flex items-center gap-3 bg-background-50 border border-background-200 rounded-lg p-3">
                      <span className="text-xs font-medium text-foreground-400 w-16 whitespace-nowrap">Option {idx + 1}</span>
                      <input
                        type="text"
                        value={v.name}
                        onChange={(e) => updateVariant(v.id, 'name', e.target.value)}
                        placeholder="e.g. Half"
                        className="flex-1 px-3 py-2 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300"
                      />
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-foreground-500">₹</span>
                        <input
                          type="number"
                          value={v.price}
                          onChange={(e) => updateVariant(v.id, 'price', e.target.value)}
                          placeholder="190"
                          className="w-20 px-3 py-2 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300"
                        />
                      </div>
                      <button
                        onClick={() => removeVariant(v.id)}
                        disabled={form.variants.length <= 1}
                        className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Remove"
                      >
                        <i className="ri-close-line text-sm text-red-500"></i>
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={addVariant}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i> Add another variant
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3 bg-background-50 border border-background-200 rounded-lg p-3">
                  <span className="text-xs font-medium text-foreground-400 w-16 whitespace-nowrap">Price *</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-foreground-500">₹</span>
                    <input
                      type="number"
                      value={form.price}
                      onChange={(e) => setForm({ ...form, price: e.target.value })}
                      placeholder="190"
                      className="w-28 px-3 py-2 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300"
                    />
                  </div>
                </div>
              )}

              {/* ── Section 4: Add-ons ── */}
              <SectionLabel icon="ri-add-circle-line" title="Add-ons & Customizations" subtitle="Extras like toppings, sides, and drinks" />

              {form.addons.length === 0 ? (
                <div className="text-center py-4 border border-dashed border-background-200 rounded-lg">
                  <p className="text-xs text-foreground-400 mb-2">No add-ons configured for this item</p>
                  <button
                    onClick={addAddon}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i> Add customization
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {form.addons.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 bg-background-50 border border-background-200 rounded-lg p-3">
                      <input
                        type="text"
                        value={a.name}
                        onChange={(e) => updateAddon(a.id, 'name', e.target.value)}
                        placeholder="e.g. Extra Cheese"
                        className="flex-1 px-3 py-2 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300"
                      />
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-foreground-500">₹</span>
                        <input
                          type="number"
                          value={a.price}
                          onChange={(e) => updateAddon(a.id, 'price', e.target.value)}
                          placeholder="30"
                          className="w-20 px-3 py-2 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300"
                        />
                      </div>
                      <button
                        onClick={() => removeAddon(a.id)}
                        className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-red-50 transition-colors cursor-pointer"
                        title="Remove"
                      >
                        <i className="ri-close-line text-sm text-red-500"></i>
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={addAddon}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i> Add another customization
                  </button>
                </div>
              )}

              {/* ── Section 5: Settings ── */}
              <SectionLabel icon="ri-settings-3-line" title="Settings" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Prep Time (min)</label>
                  <input type="number" value={form.prepTime} onChange={(e) => setForm({ ...form, prepTime: e.target.value })} placeholder="15" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                  <p className="text-[11px] text-foreground-400 mt-1">Used for KDS timers & late-order alerts</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">GST Rate (%)</label>
                  <select value={form.gstRate} onChange={(e) => setForm({ ...form, gstRate: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer">
                    <option value="0">0%</option>
                    <option value="5">5%</option>
                    <option value="12">12%</option>
                    <option value="18">18%</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">HSN / SAC code</label>
                  <input type="text" inputMode="numeric" value={form.hsnCode} onChange={(e) => setForm({ ...form, hsnCode: e.target.value })} placeholder="9963 (restaurant service)" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <label className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
                  <input type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} className="rounded border-background-300 text-primary-500 focus:ring-primary-300 cursor-pointer" />
                  Available for ordering
                </label>
                <label className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
                  <input type="checkbox" checked={form.recommended} onChange={(e) => setForm({ ...form, recommended: e.target.checked })} className="rounded border-background-300 text-accent-500 focus:ring-accent-300 cursor-pointer" />
                  Mark as Bestseller
                </label>
              </div>

              {/* Channel availability — which order types this item shows on */}
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Channel availability</label>
                <div className="grid grid-cols-3 gap-2.5">
                  {([
                    { key: 'dineIn' as const, label: 'Dine-in', icon: 'ri-restaurant-2-line' },
                    { key: 'takeaway' as const, label: 'Takeaway', icon: 'ri-takeaway-line' },
                    { key: 'online' as const, label: 'Online', icon: 'ri-global-line' },
                  ]).map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setForm({ ...form, [c.key]: !form[c.key] })}
                      className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors cursor-pointer ${
                        form[c.key]
                          ? 'border-secondary-500 bg-secondary-50 text-secondary-700'
                          : 'border-background-200 bg-background-50 text-foreground-400'
                      }`}
                    >
                      <i className={`${c.icon} text-base`}></i>
                      {c.label}
                      {form[c.key] && <i className="ri-check-line text-xs"></i>}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-foreground-400 mt-1.5">Turn a channel off to hide this item from that order type only.</p>
              </div>

              {formError && (
                <p className="text-sm text-red-600 flex items-center gap-1.5"><i className="ri-error-warning-line"></i>{formError}</p>
              )}

              {/* ── Action Buttons ── */}
              <div className="flex items-center gap-3 pt-4 border-t border-background-200">
                <button onClick={() => void handleSave()} disabled={saving} className="px-6 py-2.5 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed">
                  {saving ? 'Saving...' : editingItem ? 'Save Changes' : 'Add Item to Menu'}
                </button>
                <button onClick={() => { setModalOpen(false); setEditingItem(null); }} className="px-6 py-2.5 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Categories Modal */}
      {catModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setCatModalOpen(false)}></div>
          <div className="relative w-full max-w-md bg-background-50 rounded-xl shadow-lg mx-4 animate-[slideInUp_0.2s_ease-out] max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-background-50 border-b border-background-200 px-5 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="font-semibold text-foreground-800">Manage Categories</h3>
                <p className="text-xs text-foreground-400 mt-0.5">Add, rename or delete menu categories</p>
              </div>
              <button
                onClick={() => setCatModalOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Add new */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void handleAddCategory(); }}
                  placeholder="New category name"
                  className="flex-1 px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
                <button
                  onClick={() => void handleAddCategory()}
                  disabled={catBusy || !newCatName.trim()}
                  className="px-4 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>

              {/* Existing categories */}
              {categories.length === 0 ? (
                <div className="text-center py-6 border border-dashed border-background-200 rounded-lg">
                  <p className="text-xs text-foreground-400">No categories yet — add your first one above</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <div key={cat.id} className="flex items-center gap-2 bg-background-50 border border-background-200 rounded-lg p-2.5">
                      <div className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon || 'ri-price-tag-3-line'} text-xs text-foreground-500`}></i>
                      </div>
                      <input
                        type="text"
                        value={catEdits[cat.id] ?? cat.name}
                        onChange={(e) => setCatEdits((prev) => ({ ...prev, [cat.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') void handleRenameCategory(cat); }}
                        className="flex-1 px-3 py-1.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300"
                      />
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{countFor(cat.id)} items</span>
                      {(catEdits[cat.id] ?? cat.name).trim() !== cat.name && (
                        <button
                          onClick={() => void handleRenameCategory(cat)}
                          disabled={catBusy}
                          className="px-2 py-1.5 rounded text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                          title="Save name"
                        >
                          Save
                        </button>
                      )}
                      <button
                        onClick={() => void handleDeleteCategory(cat)}
                        disabled={catBusy}
                        className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-60"
                        title="Delete category"
                      >
                        <i className="ri-delete-bin-line text-sm text-red-500"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {catError && (
                <p className="text-sm text-red-600 flex items-center gap-1.5"><i className="ri-error-warning-line"></i>{catError}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative w-full max-w-sm bg-background-50 rounded-xl shadow-lg mx-4 p-5 animate-[slideInUp_0.15s_ease-out]">
            <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
              <i className="ri-delete-bin-line text-red-500 text-lg"></i>
            </div>
            <h4 className="text-center font-semibold text-foreground-800 mb-1">Delete Item?</h4>
            <p className="text-center text-sm text-foreground-500 mb-4">
              "{deleteTarget.name}" will be permanently removed from the menu.
            </p>
            <div className="flex items-center gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={() => void handleDelete(deleteTarget)} disabled={deleting} className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-red-500 text-background-50 hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed">{deleting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionLabel({ icon, title, subtitle }: { icon: string; title: string; subtitle?: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
        <i className={`${icon} text-sm text-foreground-500`}></i>
      </div>
      <div>
        <p className="text-sm font-semibold text-foreground-800">{title}</p>
        {subtitle && <p className="text-xs text-foreground-400">{subtitle}</p>}
      </div>
    </div>
  );
}

function MenuItemRow({ item, categoryName, selected, onToggleSelect, onToggleAvailable, onEdit, onDelete }: {
  item: MenuItem;
  categoryName: string;
  selected: boolean;
  onToggleSelect: () => void;
  onToggleAvailable: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const variants = item.variants ?? [];
  const foodDot = item.foodType === 'veg'
    ? { border: 'border-green-600', dot: 'bg-green-600' }
    : item.foodType === 'egg'
      ? { border: 'border-amber-500', dot: 'bg-amber-500' }
      : { border: 'border-red-500', dot: 'bg-red-500' };
  const priceLabel = variants.length >= 2
    ? `₹${Math.min(...variants.map((v) => v.price))} – ₹${Math.max(...variants.map((v) => v.price))}`
    : `₹${item.price}`;

  return (
    <div className={`flex items-center gap-3 px-3 py-2.5 bg-background-50 hover:bg-background-100/60 transition-colors ${item.available ? '' : 'opacity-60'}`}>
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggleSelect}
        className="rounded border-background-300 text-primary-500 focus:ring-primary-300 cursor-pointer shrink-0"
      />

      {/* Thumb */}
      <div className="w-10 h-10 rounded-md overflow-hidden bg-background-100 shrink-0">
        {item.image
          ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><i className="ri-restaurant-2-line text-foreground-200"></i></div>}
      </div>

      {/* Food type dot */}
      <span className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center shrink-0 ${foodDot.border}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${foodDot.dot}`}></span>
      </span>

      {/* Name + meta */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-foreground-800 truncate">{item.name}</p>
          {!item.available && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium shrink-0">86'd</span>}
          {item.recommended && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-800 font-medium shrink-0 flex items-center gap-0.5"><i className="ri-star-fill text-[9px]"></i>Bestseller</span>}
        </div>
        <p className="text-xs text-foreground-400 truncate">{categoryName || 'Uncategorised'}</p>
      </div>

      {/* Price */}
      <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap shrink-0 w-24 text-right">{priceLabel}</span>

      {/* On/Off toggle */}
      <button
        onClick={onToggleAvailable}
        className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${item.available ? 'bg-green-500' : 'bg-background-300'}`}
        title={item.available ? 'Tap to turn off (86)' : 'Tap to turn on'}
        aria-label={item.available ? 'Mark unavailable' : 'Mark available'}
      >
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 shadow transition-all ${item.available ? 'left-[22px]' : 'left-0.5'}`}></span>
      </button>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={onEdit} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer" title="Edit">
          <i className="ri-edit-line text-sm"></i>
        </button>
        <button onClick={onDelete} className="w-7 h-7 rounded-md flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors cursor-pointer" title="Delete">
          <i className="ri-delete-bin-line text-sm"></i>
        </button>
      </div>
    </div>
  );
}

function MenuItemCard({ item, selected, onToggleSelect, onToggleAvailable, onEdit, onDelete }: {
  item: MenuItem;
  selected: boolean;
  onToggleSelect: () => void;
  onToggleAvailable: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const variants = item.variants ?? [];
  const addons = item.addons ?? [];

  const foodDot = item.foodType === 'veg'
    ? { border: 'border-green-600', dot: 'bg-green-600' }
    : item.foodType === 'egg'
      ? { border: 'border-amber-500', dot: 'bg-amber-500' }
      : { border: 'border-red-500', dot: 'bg-red-500' };

  const priceLabel = variants.length >= 2
    ? `₹${Math.min(...variants.map((v) => v.price))} – ₹${Math.max(...variants.map((v) => v.price))}`
    : `₹${item.price}`;

  return (
    <div
      className={`relative bg-background-50 border rounded-lg overflow-hidden transition-all group ${
        selected
          ? 'border-primary-300 ring-1 ring-primary-200'
          : item.available
            ? 'border-background-200 hover:border-background-300'
            : 'border-background-200 opacity-50'
      }`}
    >
      {/* Image */}
      <div className="relative w-full h-24 overflow-hidden bg-background-100">
        {item.image ? (
          <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <i className="ri-restaurant-2-line text-3xl text-foreground-200"></i>
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-start justify-between p-2 opacity-0 group-hover:opacity-100">
          <div className="flex items-center gap-1">
            <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="w-7 h-7 rounded-full bg-background-50/90 hover:bg-background-50 flex items-center justify-center transition-colors cursor-pointer shadow-sm" title="Edit">
              <i className="ri-edit-line text-xs text-foreground-700"></i>
            </button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="w-7 h-7 rounded-full bg-background-50/90 hover:bg-red-50 flex items-center justify-center transition-colors cursor-pointer shadow-sm" title="Delete">
              <i className="ri-delete-bin-line text-xs text-red-500"></i>
            </button>
          </div>
          <div>
            <input type="checkbox" checked={selected} onChange={(e) => { e.stopPropagation(); onToggleSelect(); }} onClick={(e) => e.stopPropagation()} className="rounded border-background-300 text-primary-500 focus:ring-primary-300 cursor-pointer" />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-3">
        {/* Food type + badges */}
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <span className={`w-3 h-3 rounded-sm border flex items-center justify-center flex-shrink-0 ${foodDot.border}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${foodDot.dot}`}></span>
          </span>
          <span className="text-xs text-foreground-400 font-medium uppercase">{item.foodType === 'egg' ? 'Egg' : item.foodType === 'veg' ? 'Veg' : 'Non-Veg'}</span>
          {!item.available && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">86'd</span>
          )}
          {item.recommended && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 font-medium flex items-center gap-0.5">
              <i className="ri-star-fill text-[10px]"></i>Bestseller
            </span>
          )}
        </div>

        {/* Name + Price */}
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-sm font-semibold text-foreground-800 leading-tight line-clamp-1">{item.name}</h3>
          <span className="text-sm font-bold text-foreground-900 whitespace-nowrap">{priceLabel}</span>
        </div>

        {/* Description */}
        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-2.5">{item.description ?? ''}</p>

        {/* Tags for variants & addons */}
        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
          {variants.length >= 2 && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-800 font-medium whitespace-nowrap">
              {variants.length} sizes
            </span>
          )}
          {addons.length > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-background-100 text-foreground-600 font-medium whitespace-nowrap">
              +{addons.length} add-ons
            </span>
          )}
        </div>

        {/* Footer meta */}
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <span>{item.prepTime} min prep</span>
          <span>GST {item.gstRate}%</span>
        </div>

        {/* One-tap availability toggle */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleAvailable(); }}
          className={`mt-2.5 w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors cursor-pointer ${
            item.available
              ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
              : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
          }`}
          title={item.available ? 'Tap to mark unavailable (86)' : 'Tap to mark available'}
        >
          <span>{item.available ? 'Available' : 'Unavailable'}</span>
          <span className={`relative w-8 h-4 rounded-full transition-colors ${item.available ? 'bg-green-500' : 'bg-background-300'}`}>
            <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-background-50 transition-transform ${item.available ? 'translate-x-4' : 'translate-x-0.5'}`}></span>
          </span>
        </button>
      </div>
    </div>
  );
}
